import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { openDetails } from 'store/actions/uiActions';
import { BuildingTable } from 'components/Tables/BuildingTable';
import { useNavigate, useParams } from 'react-router-dom';
import { Dropdown, IconGraphy, Loading } from '@leapeasy/ui-kit';
import {
  failedNotif,
  getNotifFromDetailAction,
  getNotifToDetailAction,
} from 'store/actions/notifActions';
import { LandlordDetailsCard } from './LandlordDetailsCard';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { DetailPageState } from 'components/DetailPageState';
import { useDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';
import { useBuildingListFetch } from 'hooks/useBuildingListFetch';
import { BOOLEAN_YES_NO_OPTIONS } from 'utils/formatBooleanYesNo';

const parseNotificationDetailFilters = (searchParams, _paramKey, fallback) => {
  const propertyTypeParam = searchParams.get('propertyType') || searchParams.get('buildingType');
  return {
    selectedBType: propertyTypeParam
      ? propertyTypeParam.split(',').filter(Boolean)
      : fallback.selectedBType,
    isSHouse: searchParams.get('studentHousing')
      ? searchParams.get('studentHousing').split(',').filter(Boolean)
      : fallback.isSHouse,
  };
};

const serializeNotificationDetailFilters = (filters) => ({
  propertyType:
    Array.isArray(filters.selectedBType) && filters.selectedBType.length > 0
      ? filters.selectedBType.join(',')
      : '',
  studentHousing:
    Array.isArray(filters.isSHouse) && filters.isSHouse.length > 0
      ? filters.isSHouse.join(',')
      : '',
});

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const NotificationDetail = ({ type }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requestId } = useParams();

  let menuItemJson = useSelector((state) => state.getIn(['ui', 'menuItem']));
  let landlord = useSelector((state) => state.getIn(['ui', 'details']));
  const notifToDetail = useSelector((state) => state.getIn(['notification', 'msgToDetail']));
  const notifFromDetail = useSelector((state) => state.getIn(['notification', 'msgFromDetail']));
  const landlordData = landlord ? landlord.get('data') : null;
  const notifDetail = type === 'to' ? notifToDetail : notifFromDetail;
  const notifDetailJSON = useImmutableJs(notifDetail);
  const landlordFallbackJSON = useImmutableJs(landlordData);
  const hasUiFallback = Boolean(landlordData);
  const {
    data: landlordJson,
    isLoading: detailLoading,
    hasError: detailHasError,
  } = useDetailPageState({
    routeId: requestId,
    detailRecord: notifDetailJSON,
    hasUiFallback,
    fallbackData: landlordFallbackJSON,
  });
  let buildings = useSelector((state) => state.getIn(['property', 'building']));
  const buildingsJSON = useImmutableJs(buildings);

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Property..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: {
      selectedBType: [],
      isSHouse: [],
    },
    parseExtraFilters: parseNotificationDetailFilters,
    serializeExtraFilters: serializeNotificationDetailFilters,
  });

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(buildingsJSON?.total) || 0,
    isLoading: Boolean(buildingsJSON?.isLoading),
  });

  const [selectedBType, selSelectedBType] = useState([]);
  const [isSHouse, setIsSHouse] = useState([]);
  const [selectedBTypeDraft, setSelectedBTypeDraft] = useState([]);
  const [isSHouseDraft, setIsSHouseDraft] = useState([]);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedBType = Array.isArray(extraFilters.selectedBType)
      ? extraFilters.selectedBType
      : [];
    const nextIsSHouse = Array.isArray(extraFilters.isSHouse) ? extraFilters.isSHouse : [];
    selSelectedBType((prev) =>
      areArraysEqual(prev, nextSelectedBType) ? prev : nextSelectedBType,
    );
    setIsSHouse((prev) => (areArraysEqual(prev, nextIsSHouse) ? prev : nextIsSHouse));
    setSelectedBTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedBType) ? prev : nextSelectedBType,
    );
    setIsSHouseDraft((prev) => (areArraysEqual(prev, nextIsSHouse) ? prev : nextIsSHouse));
  }, [extraFilters]);

  useBuildingListFetch({
    propertyId: landlordJson?.property,
    filter,
    pagination,
    sortOptions,
    selectedBType,
    isSHouse,
    enabled: Boolean(landlordJson?.property) && !detailLoading && !detailHasError,
  });

  // Load detail once per URL id — do not depend on landlordMap (it updates after fetch → infinite loop).
  useEffect(() => {
    if (!requestId) return;
    if (type === 'to') {
      dispatch(getNotifToDetailAction(requestId));
    } else {
      dispatch(getNotifFromDetailAction(requestId));
    }
  }, [dispatch, requestId, type]);

  const onRetryDetail = useCallback(() => {
    if (!requestId) return;
    if (type === 'to') {
      dispatch(getNotifToDetailAction(requestId));
    } else {
      dispatch(getNotifFromDetailAction(requestId));
    }
  }, [dispatch, requestId, type]);

  // Legacy: detail opened without :requestId in path (relies on ui.details).
  useEffect(() => {
    if (requestId) return;
    if (type === 'to') {
      if (
        !(
          menuItemJson &&
          (menuItemJson === 'notification' || menuItemJson === 'add-users.notification') &&
          landlord &&
          landlordData
        )
      ) {
        navigate(-1);
      }
    } else if (
      !(menuItemJson && menuItemJson === 'notification.from' && landlord && landlordData)
    ) {
      navigate(-1);
    }
  }, [menuItemJson, landlord, landlordData, requestId, type, navigate]);

  const onClickBuilding = useCallback(
    (building) => {
      const buildingId = building?.building_id;
      if (!buildingId) {
        dispatch(failedNotif('Property detail ID is missing.'));
        return;
      }
      dispatch(
        openDetails({
          type: 'property',
          data: building,
        }),
      );
      navigate(`/property/properties/${buildingId}/detail/report`);
    },
    [dispatch, navigate],
  );
  const resetFunc = () => {
    selSelectedBType([]);
    setIsSHouse([]);
    setSelectedBTypeDraft([]);
    setIsSHouseDraft([]);
    setExtraFilters({
      selectedBType: [],
      isSHouse: [],
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  };
  const applyFunc = () => {
    selSelectedBType(selectedBTypeDraft);
    setIsSHouse(isSHouseDraft);
    setExtraFilters({
      selectedBType: selectedBTypeDraft,
      isSHouse: isSHouseDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  };

  return (
    <DashboardLayoutContainer>
      <ArrowDiv onClick={() => navigate(-1)}>
        <IconGraphy icon="Arrow.ArrowBack" />
        <p>Notification</p>
      </ArrowDiv>
      <DetailPageState
        isLoading={detailLoading}
        hasError={detailHasError}
        onRetry={onRetryDetail}
        loadingLabel="Loading notification details..."
        skeletonVariant="notification-card"
        compact
      >
        <LandlordDetailsCard detail={landlordJson} />
      </DetailPageState>
      {buildings ? (
        <React.Fragment>
          <CustomBox>
            {buildingsJSON.isLoading && (
              <LoadingEffect>
                <Loading size={'large'} />
              </LoadingEffect>
            )}
            <BuildingTable
              buildings={buildingsJSON.data}
              filter={filter}
              onChangeFilter={setFilter}
              dropFilter={{
                component: (
                  <DropFilterContainer>
                    <Dropdown
                      onChange={(e) => {
                        setSelectedBTypeDraft(e.target.value);
                      }}
                      options={[
                        {
                          text: 'Guarantor Waiver+LDR',
                          value: 'Event Process;Auto Enroll',
                        },
                        {
                          text: 'Rent Guaranty',
                          value: 'Auto Enroll',
                        },
                      ]}
                      value={selectedBTypeDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="Property Type"
                      label="Property Type"
                      style={{
                        width: '100%',
                      }}
                    />

                    <Dropdown
                      onChange={(e) => {
                        setIsSHouseDraft(e.target.value);
                      }}
                      options={BOOLEAN_YES_NO_OPTIONS}
                      value={isSHouseDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="Student Housing"
                      label="Student Housing"
                      style={{
                        width: '100%',
                      }}
                    />
                  </DropFilterContainer>
                ),
                resetFunc: resetFunc,
                applyFunc: applyFunc,
              }}
              pagination={{
                ...pagination,
                totalItems: buildingsJSON.total,
                totalPages: Math.ceil(buildingsJSON.total / pagination.rowsPerPage),
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickBuilding={onClickBuilding}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;

const ArrowDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  cursor: pointer;
  width: min-content;
  margin-bottom: 20px;

  p {
    margin: 0;
    color: #2e0f40;
    font-weight: bold;
    font-size: 20px;
  }
`;
